import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { nfpSummary } from "@/lib/macro-report-summary";
import { getUsEmployment } from "@/lib/nfp.functions";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-nfp-employment";
const TITLE = "NFP and Gold: Jobs Report Impact on XAUUSD | XAU Pro Academy";
const DESC =
  "Free US employment tracker for gold traders. Nonfarm payrolls, unemployment rate and wage growth from official BLS data, with how gold moved in each release month.";

const FAQ = [
  { q: "Why does the NFP report move gold?", a: "The jobs report shapes expectations for Federal Reserve policy. Strong hiring and firm wages can lift rate expectations and the US dollar, which often pressures gold. Weak hiring or rising unemployment tends to do the opposite." },
  { q: "When is the NFP report released?", a: "The Bureau of Labor Statistics usually publishes the Employment Situation report on the first Friday of each month at 08:30 New York time, covering the prior month." },
  { q: "Which part of the report matters most?", a: "Markets react to the surprise versus forecast in nonfarm payrolls first, then to the unemployment rate and average hourly earnings. Revisions to prior months can change the reading of the headline number." },
  { q: "Should I trade the NFP release directly?", a: "The release candle often sweeps liquidity on both sides within seconds and spreads widen sharply. A disciplined approach waits for the spike to complete, then requires structure, displacement and defined invalidation before any execution." },
  { q: "Where does this data come from?", a: "Payrolls, unemployment and wages come from the official BLS public API (seasonally adjusted series). The monthly payroll change is computed from the payroll level. Gold monthly returns come from our XAUUSD monthly history." },
];

export const Route = createFileRoute("/gold-nfp-employment")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "NFP gold, nonfarm payrolls gold price, jobs report XAUUSD, unemployment rate gold, NFP gold impact, US employment gold tracker" },
      { property: "og:title", content: "NFP and Gold: US Jobs Report Impact Tracker" },
      { property: "og:description", content: "Official BLS payrolls, unemployment and wages with gold reaction per release month. Clear for beginners, useful for experienced traders." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "US Nonfarm Payrolls, Unemployment and Gold Monthly Reaction",
          description: "Monthly US nonfarm payroll change, unemployment rate and wage growth with XAUUSD return in each release month.",
          url: `${SITE}${PATH}`,
          license: "https://xauproacademy.com/terms", isAccessibleForFree: true, inLanguage: "en", variableMeasured: ["Nonfarm payroll change", "Unemployment rate", "Average hourly earnings growth", "XAUUSD release month return"], temporalCoverage: "2020-01-01/..", distribution: [{ "@type": "DataDownload", encodingFormat: "text/html", contentUrl: `${SITE}${PATH}` }], dateModified: new Date().toISOString().slice(0, 10), creator: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
          isBasedOn: "https://www.bls.gov/ces/",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "NFP and Gold", item: `${SITE}${PATH}` },
          ],
        }),
      },
    ],
  }),
  loader: () => getUsEmployment(),
  staleTime: 60 * 60 * 1000,
  component: NfpPage,
});

const monthLabel = (p: string) => {
  const [y, m] = p.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
};
const fp = (x: number | null, sign = false) => (x == null ? "n/a" : `${sign && x > 0 ? "+" : ""}${x.toFixed(1)}%`);
const fk = (x: number | null) => (x == null ? "n/a" : `${x > 0 ? "+" : ""}${x.toLocaleString("en-US")}K`);


function NfpPage() {
  const data = Route.useLoaderData();
  const months = data.months;
  const latest = months[months.length - 1];
  const [sel, setSel] = useState(latest?.period ?? "");
  const idx = Math.max(0, months.findIndex((m) => m.period === sel));
  const cur = months[idx] ?? latest;
  const prev = months[idx - 1];
  const win = months.slice(Math.max(0, idx - 2), idx + 1);
  const avg3 = win.length === 3 ? Math.round(win.reduce((s, m) => s + (m.payrollsK ?? 0), 0) / 3) : null;
  const isHistorical = !!latest && cur?.period !== latest.period;
  const series = useMemo(() => months.slice(-36).map((m) => ({ ...m, label: monthLabel(m.period) })), [months]);
  const reactions = useMemo(() => months.filter((m) => m.goldReleaseMonthPct != null).slice(-24).map((m) => ({ ...m, label: monthLabel(m.period) })), [months]);

  const [vars, setVars] = useState<Record<string, string>>({});
  useEffect(() => {
    const cs = getComputedStyle(document.documentElement);
    const out: Record<string, string> = {};
    for (const k of ["gold", "chart-silver", "border", "muted-foreground", "destructive", "card", "foreground", "muted"]) out[k] = cs.getPropertyValue(`--${k}`).trim();
    setVars(out);
  }, []);
  const c = (k: string) => vars[k] || "currentColor";
  const tip = { contentStyle: { background: c("card"), border: `1px solid ${c("border")}` }, labelStyle: { color: c("foreground") }, itemStyle: { color: c("foreground") }, cursor: { fill: c("muted"), fillOpacity: 0.25 } };

  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container relative mx-auto max-w-6xl px-4 pb-14 pt-16 md:pt-24">
          <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            Institutional Tools · BLS Data
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold tracking-tight md:text-5xl">
            NFP and Gold: <span className="text-gradient-gold">US jobs report impact</span> on XAUUSD
          </h1>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Track US nonfarm payrolls, the unemployment rate and wage growth from official data, see whether the labour
            market is strengthening or softening, and review how gold behaved in each release month.
          </p>
          {latest && (
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Latest reference month {monthLabel(latest.period)} · First Friday 08:30 ET · Source: BLS
            </p>
          )}
        </div>
      </section>

      {!latest ? (
        <section className="container mx-auto max-w-6xl px-4 py-16 text-center text-muted-foreground">{data.error ?? "Employment data is temporarily unavailable."}</section>
      ) : (
        <>
          {data.stale && <p role="status" className="container mx-auto max-w-6xl px-4 pt-6 text-sm text-gold">The source is temporarily unavailable. Showing the last successfully retrieved data.</p>}
          <section className="sticky top-16 z-20 border-b border-border/60 bg-background/90 backdrop-blur">
            <div className="container mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
              <label className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="font-mono uppercase tracking-[0.18em]">Report month</span>
                <select aria-label="Report month" value={cur.period} onChange={(e) => setSel(e.target.value)} className="rounded-md border border-border bg-card px-3 py-2 font-mono text-sm text-foreground">
                  {months.slice(-12).reverse().map((m) => <option key={m.period} value={m.period}>{monthLabel(m.period)}{m.period === latest.period ? " (latest)" : ""}</option>)}
                </select>
              </label>
              {isHistorical ? (
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-gold">Viewing {monthLabel(cur.period)}</span>
                  <Button size="sm" variant="outline" onClick={() => setSel(latest.period)}>Latest report</Button>
                </div>
              ) : <span className="font-mono text-xs uppercase text-muted-foreground">Latest report</span>}
            </div>
          </section>

          <section className="container mx-auto max-w-6xl px-4 pt-10">
            <div className="rounded-xl border border-gold/30 bg-card p-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">This report in 30 seconds · {monthLabel(cur.period)}</p>
              <dl className="mt-4 grid gap-4 md:grid-cols-2">
                {nfpSummary(cur, prev, avg3).map((l) => (
                  <div key={l.label}>
                    <dt className="text-sm font-semibold">{l.label}</dt>
                    <dd className="mt-1 text-sm text-muted-foreground">{l.text}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-4 text-xs text-muted-foreground">Context for bias only. Not a trade signal.</p>
            </div>
          </section>

          <section className="container mx-auto max-w-6xl px-4 py-10">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Nonfarm payrolls" value={fk(cur.payrollsK)} sub={avg3 != null ? `3m avg ${fk(avg3)}` : ""} />
              <Stat label="Unemployment rate" value={cur.unemployment == null ? "n/a" : `${cur.unemployment.toFixed(1)}%`} sub={prev?.unemployment != null ? `prior ${prev.unemployment.toFixed(1)}%` : ""} />
              <Stat label="Hourly earnings y/y" value={fp(cur.wagesYoY)} sub={`m/m ${fp(cur.wagesMoM, true)}`} />
              <Stat label="Gold in release month" value={fp(cur.goldReleaseMonthPct, true)} sub={cur.goldReleaseMonthPct == null ? "Month in progress" : "XAUUSD open to close"} tone={cur.goldReleaseMonthPct == null ? "" : cur.goldReleaseMonthPct >= 0 ? "text-gold" : "text-destructive"} />
            </div>

            <div className="mt-10 grid gap-8 lg:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-5">
                <h2 className="font-display text-xl font-bold">Monthly job gains, 3 years</h2>
                <p className="mt-1 text-sm text-muted-foreground">Nonfarm payroll change in thousands. Red bars mark job losses.</p>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={series}>
                      <CartesianGrid stroke={c("border")} strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fill: c("muted-foreground"), fontSize: 11 }} minTickGap={30} />
                      <YAxis tick={{ fill: c("muted-foreground"), fontSize: 11 }} width={44} />
                      <Tooltip {...tip} formatter={(v: number) => fk(v)} />
                      <ReferenceLine y={0} stroke={c("muted-foreground")} />
                      <Bar dataKey="payrollsK" name="Payrolls">
                        {series.map((m) => <Cell key={m.period} fill={(m.payrollsK ?? 0) >= 0 ? c("gold") : c("destructive")} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <h2 className="font-display text-xl font-bold">Unemployment rate, 3 years</h2>
                <p className="mt-1 text-sm text-muted-foreground">A rising rate signals a loosening labour market.</p>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series}>
                      <CartesianGrid stroke={c("border")} strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fill: c("muted-foreground"), fontSize: 11 }} minTickGap={30} />
                      <YAxis tick={{ fill: c("muted-foreground"), fontSize: 11 }} unit="%" width={44} domain={["dataMin - 0.2", "dataMax + 0.2"]} />
                      <Tooltip {...tip} formatter={(v: number) => `${v.toFixed(1)}%`} />
                      <Line type="monotone" dataKey="unemployment" name="Unemployment" stroke={c("chart-silver")} dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {reactions.length > 0 && (
              <div className="mt-8 rounded-xl border border-border bg-card p-5">
                <h2 className="font-display text-xl font-bold">Gold in each NFP release month</h2>
                <p className="mt-1 text-sm text-muted-foreground">Labelled by report reference month. The bar shows the XAUUSD return of the following month, when that report was published.</p>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reactions}>
                      <CartesianGrid stroke={c("border")} strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fill: c("muted-foreground"), fontSize: 11 }} minTickGap={30} />
                      <YAxis tick={{ fill: c("muted-foreground"), fontSize: 11 }} unit="%" width={44} />
                      <Tooltip {...tip} formatter={(v: number) => fp(v, true)} />
                      <ReferenceLine y={0} stroke={c("muted-foreground")} />
                      <Bar dataKey="goldReleaseMonthPct" name="Gold">
                        {reactions.map((m) => <Cell key={m.period} fill={(m.goldReleaseMonthPct ?? 0) >= 0 ? c("gold") : c("destructive")} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    {["Month", "Payrolls", "Unemployment", "Wages y/y", "Wages m/m", "Gold release month"].map((h) => <th key={h} className="px-4 py-3 text-left">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {months.slice(-12).reverse().map((m) => (
                    <tr key={m.period} className={`border-b border-border/50 ${m.period === cur.period ? "bg-gold-muted" : ""}`}>
                      <td className="px-4 py-2 font-mono">{monthLabel(m.period)}</td>
                      <td className={`px-4 py-2 ${(m.payrollsK ?? 0) < 0 ? "text-destructive" : ""}`}>{fk(m.payrollsK)}</td>
                      <td className="px-4 py-2">{m.unemployment == null ? "n/a" : `${m.unemployment.toFixed(1)}%`}</td>
                      <td className="px-4 py-2">{fp(m.wagesYoY)}</td>
                      <td className="px-4 py-2">{fp(m.wagesMoM, true)}</td>
                      <td className={`px-4 py-2 ${m.goldReleaseMonthPct == null ? "" : m.goldReleaseMonthPct >= 0 ? "text-gold" : "text-destructive"}`}>{fp(m.goldReleaseMonthPct, true)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Latest values are first estimates and may be revised in the following two reports.</p>

            <div className="mt-10 rounded-xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-bold">Execution around the NFP release</h2>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
                <li>The first minutes after 08:30 ET frequently sweep liquidity on both sides. Avoid committing during the initial spike.</li>
                <li>Wait for the release candle range to complete, then look for a body close beyond structure with displacement.</li>
                <li>Prefer entries from a fresh POI formed by the move, with invalidation beyond the swept liquidity.</li>
                <li>Spreads and slippage widen sharply. Size for the wider stop, or stand aside. Missing the move is acceptable.</li>
              </ul>
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                <Link to="/gold-inflation-cpi" className="text-gold hover:underline">US CPI and Gold</Link>
                <Link to="/gold-cot-report" className="text-gold hover:underline">Gold COT Report</Link>
                <Link to="/gold-seasonality" className="text-gold hover:underline">Gold Seasonality</Link>
                <Link to="/gold-analysis" className="text-gold hover:underline">Daily gold analysis</Link>
              </div>
            </div>
          </section>
        </>
      )}

      <section className="container mx-auto max-w-4xl px-4 pb-16">
        <h2 className="font-display text-2xl font-bold">Frequently asked questions</h2>
        <div className="mt-4 space-y-4">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-lg border border-border bg-card p-4">
              <h3 className="font-semibold">{f.q}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}

function Stat({ label, value, sub, tone = "" }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className={`mt-2 font-display text-2xl font-bold ${tone}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}
